import {
  arrangeWidgets,
  withMove,
  withVisibility,
  type DashboardLayout,
} from '@/screens/dashboard/layout';
import type { WidgetDef, WidgetKind } from '@/screens/dashboard/widgets';
import { useAuth } from '@/stores/auth';
import { usePreferences } from '@/stores/preferences';

/**
 * Settings › Dashboard: what each person wants on their own home screen, and
 * in what order.
 *
 * Only what their permissions allow is listed, so this can hide something but
 * never reveal it. Saved to their account like the other display settings, so
 * it follows them to any computer.
 */
export function DashboardPage() {
  const can = useAuth((s) => s.can);
  const layout = usePreferences((s) => s.prefs.dashboardLayout);
  const set = usePreferences((s) => s.set);

  const cards = arrangeWidgets('card', layout, can);
  const sections = arrangeWidgets('section', layout, can);

  const change = (next: DashboardLayout) => set({ dashboardLayout: next });

  return (
    <section className="settingsapp__section" aria-labelledby="settings-dashboard">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-dashboard">Dashboard</h2>
          <p className="muted settings__intro">
            Choose what your home screen shows, and the order it comes in. This is only for you —
            it does not change anyone else's screen. You only see what your own access allows.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => set({ dashboardLayout: null })}
          disabled={layout === null}
        >
          Put back the default
        </button>
      </header>

      <WidgetList
        kind="card"
        title="Figures across the top"
        intro="One number each, with a click that opens the screen behind it."
        widgets={cards.available}
        hidden={new Set(cards.hidden.map((w) => w.id))}
        onChange={change}
        layout={layout}
      />

      <WidgetList
        kind="section"
        title="Panels"
        intro="The bigger blocks below the figures: announcements, and the lists beside them."
        widgets={sections.available}
        hidden={new Set(sections.hidden.map((w) => w.id))}
        onChange={change}
        layout={layout}
      />
    </section>
  );
}

function WidgetList({
  kind,
  title,
  intro,
  widgets,
  hidden,
  layout,
  onChange,
}: {
  kind: WidgetKind;
  title: string;
  intro: string;
  widgets: WidgetDef[];
  hidden: Set<string>;
  layout: DashboardLayout | null;
  onChange: (next: DashboardLayout) => void;
}) {
  if (widgets.length === 0) return null;

  const order = widgets.map((w) => w.id);
  const shownCount = widgets.length - hidden.size;

  return (
    <div className="dashsettings">
      <h3 className="dashsettings__title">{title}</h3>
      <p className="settings__hint">{intro}</p>

      <ul className="dashsettings__list">
        {widgets.map((widget, index) => {
          const shown = !hidden.has(widget.id);
          // Something must be left: an empty dashboard looks broken rather
          // than chosen.
          const lastOne = shown && shownCount === 1;

          return (
            <li key={widget.id} className="dashsettings__row">
              <label className="dashsettings__show">
                <input
                  type="checkbox"
                  checked={shown}
                  disabled={lastOne}
                  onChange={(e) =>
                    onChange(withVisibility(layout, kind, widget.id, e.target.checked, order))
                  }
                />
                <span>
                  <span className="dashsettings__label">{widget.label}</span>
                  <span className="settings__hint">{widget.hint}</span>
                  {lastOne && (
                    <span className="settings__hint">
                      The last one showing cannot be switched off.
                    </span>
                  )}
                </span>
              </label>

              <span className="dashsettings__move">
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  aria-label={`Move ${widget.label} up`}
                  disabled={index === 0}
                  onClick={() => onChange(withMove(layout, kind, widget.id, 'up', order))}
                >
                  <span aria-hidden="true">↑</span>
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  aria-label={`Move ${widget.label} down`}
                  disabled={index === widgets.length - 1}
                  onClick={() => onChange(withMove(layout, kind, widget.id, 'down', order))}
                >
                  <span aria-hidden="true">↓</span>
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
